import { Box, Flex, HStack, Icon, Skeleton, Stack, Text } from "@chakra-ui/react";
import { StatHelpText, StatLabel, StatRoot } from "@/components/ui/stat";
import type { ReactNode } from "react";
import type { IconType } from "react-icons";

type ResponsiveStyleValue = string | number | Record<string, string | number>;

export type StatCardProps = {
    label: ReactNode;
    value: ReactNode;
    unit?: ReactNode;
    helpText?: ReactNode;
    icon?: IconType;
    accentColor?: string;
    iconBg?: string;
    iconColor?: string;
    valueColor?: string;
    valueFontSize?: ResponsiveStyleValue;
    valueAlign?: "start" | "end";
    labelColor?: string;
    labelFontSize?: ResponsiveStyleValue;
    labelTextTransform?: "none" | "uppercase";
    helpTextPlacement?: "header" | "footer";
    isLoading?: boolean;
    showAccentBorder?: boolean;
    [key: string]: any;
};

const StatCard = ({
    label,
    value,
    unit,
    helpText,
    icon,
    accentColor = "blue.600",
    iconBg = "blue.50",
    iconColor = accentColor,
    valueColor = "fg",
    valueFontSize = { base: "2xl", md: "3xl" },
    valueAlign = "start",
    labelColor = "fg.muted",
    labelFontSize = "xs",
    labelTextTransform = "uppercase",
    helpColor = "fg.muted",
    helpTextPlacement = "footer",
    isLoading = false,
    showAccentBorder = true,
    p = { base: 3, md: 4 },
    minH = "7rem",
    borderWidth = "1px",
    borderColor = "border",
    bg = "bg.panel",
    boxShadow = 'none',
    ...rootProps
}: StatCardProps) => {
    const justifyValue = valueAlign === "start" ? "flex-start" : "flex-end";
    const hasHelpText = helpText !== undefined && helpText !== null && helpText !== "";
    const hasUnit = unit !== undefined && unit !== null && unit !== "";
    const showHeaderHelp = helpTextPlacement === "header" && hasHelpText;
    const showFooterHelp = helpTextPlacement === "footer" && hasHelpText;

    return (
        <StatRoot
            p={p}
            minH={minH}
            rounded='lg'
            borderWidth={borderWidth}
            borderColor={borderColor}
            borderLeftWidth={showAccentBorder ? "4px" : undefined}
            borderLeftColor={showAccentBorder ? accentColor : undefined}
            bg={bg}
            boxShadow={boxShadow}
            {...rootProps}
        >
            <Stack gap={1} h="full" justify="space-between">
                <HStack align="start" justify="space-between" gap={1}>
                    <Stack gap={0} minW={0}>
                        <StatLabel
                            color={labelColor}
                            fontSize={labelFontSize}
                            fontWeight="semibold"
                            letterSpacing="0"
                            textTransform={labelTextTransform}
                            overflowWrap="anywhere"
                        >
                            {label}
                        </StatLabel>
                        {showHeaderHelp && (
                            <StatHelpText color={helpColor} fontSize="xs" lineHeight="1.25" overflowWrap="anywhere">
                                {helpText}
                            </StatHelpText>
                        )}
                    </Stack>
                    {icon && (
                        <Flex
                            align="center"
                            justify="center"
                            boxSize='2rem'
                            flexShrink={0}
                            rounded="full"
                            color={iconColor}
                            bg={iconBg}
                        >
                            <Icon as={icon} />
                        </Flex>
                    )}
                </HStack>

                <Stack gap={1}>
                    <Skeleton loading={isLoading}>
                        <HStack align="baseline" justify={justifyValue} gap={1}>
                            <Text
                                as="span"
                                color={valueColor}
                                fontSize={valueFontSize}
                                fontWeight="bold"
                                lineHeight="1"
                                textAlign={valueAlign === "start" ? "left" : "right"}
                                whiteSpace="nowrap"
                            >
                                {value}
                            </Text>
                            {hasUnit && (
                                <Box as="span" color={helpColor} fontSize="sm" fontWeight="medium" lineHeight="1">
                                    {unit}
                                </Box>
                            )}
                        </HStack>
                    </Skeleton>
                    {showFooterHelp && (
                        <StatHelpText color={helpColor} fontSize="xs" lineHeight="1.35">
                            {helpText}
                        </StatHelpText>
                    )}
                </Stack>
            </Stack>
        </StatRoot>
    );
};

export default StatCard;
